import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Col, Form, Row } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import errorToast from "helpers/errorToast";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { cancelApprovalRequest } from "api/services/quotation";

const CancelApprovalRequestDialog = function ({ isOpen, close, quotationId, fetchQuoteRequestData }) {
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit } = methods;

  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => setComment(""), [isOpen]);

  const onSubmit = () => {
    setIsLoading(true);
    const payload = {
      comment,
    };
    cancelApprovalRequest(quotationId, payload)
      .then(() => {
        toast(
          intl.formatMessage({
            id: "shipment-quotes.components.quotes.cancelApprovalRequestDialog.approvalRequestCanceled",
            defaultMessage: "Approval request canceled",
          }),
          { type: "success" },
        );
        fetchQuoteRequestData();
        close();
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog
      size="md"
      title={intl.formatMessage({
        id: "shipment-quotes.components.quotes.cancelApprovalRequestDialog.cancelApprovalRequest",
        defaultMessage: "Cancel approval request",
      })}
      show={isOpen}
      onHide={close}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <p className="mb-1 font-weight-bold">
            <FormattedMessage
              id="shipment-quotes.components.cancelApprovalRequestDialog.cancelPrompt"
              defaultMessage="Are you sure you want to cancel this request?"
            />
          </p>
          <p className="text-secondary">
            <FormattedMessage
              id="shipment-quotes.components.cancelApprovalRequestDialog.cancelPromptInfo"
              defaultMessage="This will cancel the approval request for this quotation. You will be able to request approval for another
              quotation. The quote itself and the quote request won’t be affected."
            />
          </p>
          <div className="mt-4">
            <Form.Control
              as="textarea"
              rows={2}
              onChange={(e) => setComment(e.target.value)}
              className=""
              placeholder={intl.formatMessage({
                id: "shipment-quotes.components.quotes.cancelApprovalRequestDialog.commentPlaceholder",
                defaultMessage: "Comments for your request (optional)",
              })}
            />
          </div>
          <Row className="mt-5">
            <Col lg={12} className="d-flex flex-row align-items-center">
              <Button
                size="lg"
                className="btn-grey"
                label={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                onClick={close}
              />
              <Button
                size="lg"
                className="ml-auto"
                isLoading={isLoading}
                variant="primary"
                label={intl.formatMessage({
                  id: "shipment-quotes.components.cancelApprovalRequestDialog.cancelConfirmation",
                  defaultMessage: "Yes, cancel request",
                })}
                type="submit"
              />
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
};

CancelApprovalRequestDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  quotationId: PropTypes.string,
  isBookAsDuplicate: PropTypes.bool,
  fetchQuoteRequestData: PropTypes.func.isRequired,
};

CancelApprovalRequestDialog.defaultProps = {
  isBookAsDuplicate: false,
  quotationId: "",
};

export default CancelApprovalRequestDialog;
